/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { updateWorkPackageRank } from '../../actions/workPackageActions';
import StoryList from './storyList';

class Table extends React.Component {
  constructor(props) {
    super(props);

    this.titleRow = undefined;
    this.leftFieldGroup = undefined;
    this.rightFieldGroup = undefined;
    this.fieldWidths = {};

    this.state = {
      listWidth: props.listWidth - 10 || 0
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateListWidthAndHeight);
    this.updateListWidthAndHeight();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.collapseLeft !== this.props.collapseLeft ||
      prevProps.collapseRight !== this.props.collapseRight ||
      prevProps.visibleFields.length !== this.props.visibleFields.length ||
      prevProps.listWidth !== this.props.listWidth ||
      prevProps.listHeight !== this.props.listHeight
    ) {
      this.updateListWidthAndHeight();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateListWidthAndHeight);
    window.removeEventListener('scroll', this.updateListWidthAndHeight);
  }

  updateListWidthAndHeight = () => {
    const bottomElementMargin = 10;

    let tableHeaderBottom;
    let listBottom;
    if (this.props.source === 'TargetDetail') {
      tableHeaderBottom = this.titleRow.offsetHeight;
      listBottom = document.getElementById('target-detail').offsetHeight;
    } else {
      tableHeaderBottom =
        this.titleRow.getBoundingClientRect().bottom + bottomElementMargin;

      // List height will be available vertical space until either the story input box or the footer
      let bottomElement = document.getElementById('add-item-input');

      // It's possible that the Add Story input isn't present, so use the footer instead
      if (!bottomElement) bottomElement = document.getElementById('adz-footer');
      listBottom = bottomElement ? bottomElement.offsetTop : 0;
    }
    const listHeight = listBottom - tableHeaderBottom;

    const columnsWidth =
      this.leftFieldGroup && this.rightFieldGroup
        ? this.leftFieldGroup.offsetWidth + this.rightFieldGroup.offsetWidth
        : 0;
    const tableHeaderWidth = this.titleRow ? this.titleRow.scrollWidth : 0;
    let listWidth =
      tableHeaderWidth > columnsWidth ? tableHeaderWidth : columnsWidth;

    if (this.props.isChildren) {
      listWidth = this.props.listWidth - 10;
    }
    this.setState({ listWidth, listHeight });
  };

  render() {
    const {
      results,
      source,
      visibleFields,
      level,
      isWorkPackageFetching,
      isDragging,
      expandedItems,
      headerColumns,
      tableFields,
      isChildren,
      parentResult,
      virtualized,
      dropDown,
      setDropdown,
      expanded,
      sortField,
      levelsDeep,
      target,
      highlightedIds,
    } = this.props;
    const { listWidth, listHeight } = this.state;
    const defaultColumnNames = [
      'Value',
      'Risk',
      'Size',
      'State',
      'empty',
      'RROE',
      'TC',
      'WSJF'
    ];

    let filteredFields = visibleFields;

    if (isChildren) {
      filteredFields = visibleFields.filter(
        (field) => field.groupLeft || defaultColumnNames.includes(field.name)
      );
    }

    return (
      <div
        className={
          isChildren
            ? `list-header-informative ${levelsDeep} `
            : `table level-${level} `
        }
      >
        <div
          ref={(e) => {
            this.titleRow = e;
          }}
          className={`story-row-title ${isChildren ? 'row' : ''}`}
        >
          <div
            ref={(e) => {
              this.leftFieldGroup = e;
            }}
            className="left-field-group"
          >
            {filteredFields
              .filter((field) => field.groupLeft)
              .map((field) => {
                const fieldName = field.name.toLowerCase().replace(' ', '-');
                if (field.name === 'empty') {
                  return (
                    <div
                      key={field.under}
                      className={`${field.under.toLowerCase()}-col`}
                    >
                      &nbsp;
                    </div>
                  );
                }
                return (
                  <div
                    key={field.name}
                    className={`${fieldName}-col ${
                      !isChildren ? `${fieldName}-col-header col-header` : ''
                    }`}
                    ref={(e) => {
                      this.fieldWidths[fieldName] = e ? e.offsetWidth : 0;
                      return e;
                    }}
                  >
                    {headerColumns
                      ? headerColumns(fieldName, field)
                      : field.name}
                  </div>
                );
              })}
          </div>
          <div
            ref={(e) => {
              this.rightFieldGroup = e;
            }}
            className="right-field-group"
          >
            {filteredFields
              .filter((field) => !field.groupLeft)
              .map((field) => {
                const fieldName = field.name.toLowerCase().replace(' ', '-');
                if (field.name === 'empty') {
                  return (
                    <div
                      key={field.under}
                      className={`${field.under.toLowerCase()}-col`}
                    >
                      &nbsp;
                    </div>
                  );
                }
                return (
                  <div
                    key={field.name}
                    className={`col-header ${fieldName}-col`}
                    ref={(e) => {
                      this.fieldWidths[fieldName] = e ? e.offsetWidth : 0;
                      return e;
                    }}
                  >
                    {headerColumns
                      ? headerColumns(fieldName, field)
                      : field.name}
                  </div>
                );
              })}
          </div>
        </div>
        <StoryList
          results={results}
          highlightedIds={highlightedIds}
          source={source}
          fields={filteredFields}
          move={this.props.updateWorkPackageRank}
          level={level || 4}
          isFetching={isWorkPackageFetching}
          listWidth={listWidth}
          listHeight={listHeight}
          fieldWidths={this.fieldWidths}
          isDragging={isDragging}
          parentTableColumns={tableFields}
          expandedItems={expandedItems}
          delete={this.props.delete}
          virtualized={virtualized}
          isChildren={isChildren}
          parentResult={parentResult}
          dropDown={dropDown}
          setDropdown={setDropdown}
          expanded={expanded}
          sortField={sortField}
          levelsDeep={levelsDeep}
          target={target}
        />
      </div>
    );
  }
}

Table.propTypes = {
  results: PropTypes.array,
  highlightedIds: PropTypes.array,
  source: PropTypes.string,
  visibleFields: PropTypes.array,
  level: PropTypes.number,
  isWorkPackageFetching: PropTypes.bool,
  isDragging: PropTypes.bool,
  expandedItems: PropTypes.array,
  tableFields: PropTypes.array,
  isChildren: PropTypes.bool,
  virtualized: PropTypes.bool,
  parentResult: PropTypes.object,
  dropDown: PropTypes.object,
  setDropdown: PropTypes.func,
  updateWorkPackageRank: PropTypes.func,
  headerColumns: PropTypes.func,
  delete: PropTypes.func,
  expanded: PropTypes.bool,
};

Table.defaultProps = {
  virtualized: true
};

const mapStateToProps = (state) => ({
  isWorkPackageFetching:
    state.workPackageReducer.isSending || state.workPackageReducer.isFetching,
  isDragging: state.notificationReducer.dragInProgress,
  expandedItems: state.listReducer.expandedItems
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateWorkPackageRank
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Table);
